import { Card, CardProps, Checkbox, Input } from "@dune/ui";
import {
	formatBuildingLabel,
	formatCurrencyPrecise,
	formatDwellingLabel,
	formatSurfaceLabel,
	getActionTypeLabel,
	getBuildingTypeLabel,
} from "@dune/utils";
import { RENOVATION_OR_EXTENSION_ACTION_TYPES } from "@dune/vars";
import { useFormContext, useWatch } from "react-hook-form";
import { ConstructionSiteDetailsFormData } from "../../../../../component/brokers/constructionSites/useConstructionSiteDetailsForm";

interface MainDetailsOperationProps extends CardProps {
	isDisabled?: boolean;
	showConstructionCost?: boolean;
	isReception?: boolean;
}

export const MainDetailsOperation = ({
	isDisabled: isForm = false,
	showConstructionCost = false,
	isReception = false,
	...cardProps
}: MainDetailsOperationProps) => {
	const { control, watch } = useFormContext<ConstructionSiteDetailsFormData>();
	const projectName = useWatch({ control, name: "projectName" });
	const operationType = useWatch({ control, name: "operationType" });
	const actionsValue = useWatch({ control, name: "actions" });
	const privateWork = actionsValue?.buildingType === "PRIVATE_WORK";
	const commonParts = actionsValue?.buildingType === "COMMON_PARTS";
	const streetNumber = useWatch({ control, name: "streetNumber" });
	const streetName = useWatch({ control, name: "streetName" });
	const postalCode = useWatch({ control, name: "postalCode" });
	const city = useWatch({ control, name: "city" });
	const taxMode = watch("taxMode");
	const constructionCost =
		taxMode === "TTC" ? watch("totalAmountTTC") : watch("totalAmountHT");
	const finalTotalAmount = watch("finalTotalAmount");

	const address =
		`${streetNumber || ""} ${streetName || ""}, ${postalCode || ""} ${city || ""}`.trim();

	const appartmentDetails =
		actionsValue?.buildingType === "APPARTMENT"
			? actionsValue.buildingDetails.APPARTMENT
			: undefined;

	const currentBuildingDetails = actionsValue?.buildingType
		? actionsValue.buildingDetails[actionsValue.buildingType]
		: undefined;

	const mixtBuildingDetails =
		actionsValue?.buildingType === "MIXT_BUILDING"
			? actionsValue.buildingDetails.MIXT_BUILDING
			: undefined;

	return (
		<Card className="p-6 space-y-6" {...cardProps}>
			{(projectName || operationType) && (
				<div className="flex flex-row gap-4">
					<Input
						disabled={isForm}
						label="Nom du projet"
						placeholder="Nom du projet"
						value={projectName}
					/>
					<Input
						disabled={isForm}
						label={"Type d'opération"}
						value={operationType}
					/>
				</div>
			)}
			<div>
				<Input
					label="Adresse"
					disabled={isForm}
					placeholder="123 Rue de la Paix, 75001 Paris"
					value={address}
				/>
			</div>

			{privateWork && (
				<div className="flex flex-row items-center gap-4">
					<div className="text-gray-700 w-1/4">Elle concerne</div>
					<Checkbox
						id="privateWork"
						label="des travaux privatifs"
						checked={privateWork || false}
						disabled={isForm}
						className="border-gray-300 disabled:bg-gray-100"
					/>
				</div>
			)}

			{commonParts && (
				<div className="flex flex-row items-center gap-4">
					<div className="text-gray-700 w-1/4">Elle concerne</div>
					<Checkbox
						id="commonParts"
						label="des parties communes"
						checked={commonParts || false}
						disabled={isForm}
						className="border-gray-300 disabled:bg-gray-100"
					/>
				</div>
			)}

			{((actionsValue?.types?.length && actionsValue.types.length > 0) ||
				actionsValue?.buildingType) && (
				<div className="space-y-4">
					{actionsValue?.types?.length &&
						actionsValue.types.length > 0 &&
						!privateWork &&
						!commonParts && (
							<div className="flex flex-row items-center gap-4">
								<div className="text-gray-700 w-1/4">Elle consiste en</div>
								<div className="grid grid-cols-2 gap-4">
									<div className="flex flex-row items-center gap-4">
										{actionsValue.types.map((type: string, index: number) => (
											<div
												key={`main-${type}`}
												className="flex flex-row items-center gap-1"
											>
												{index > 0 && <div className="">et </div>}
												<Checkbox
													id={`action-type-${type}`}
													label={getActionTypeLabel(type)}
													checked={true}
													disabled={isForm}
													className="border-gray-300 disabled:bg-gray-100"
												/>
											</div>
										))}
									</div>
									{actionsValue?.buildingType && (
										<Checkbox
											id="building-type"
											label={getBuildingTypeLabel(actionsValue.buildingType)}
											checked={true}
											disabled={isForm}
											className="border-gray-300 disabled:bg-gray-100"
										/>
									)}
								</div>
							</div>
						)}
					{actionsValue?.hasComplementaryActions &&
						actionsValue?.complementaryActions?.types &&
						actionsValue?.complementaryActions?.types?.length > 0 && (
							<div className="flex flex-row items-center gap-4">
								<div className="text-gray-700 w-1/4">ET</div>
								<div className="grid grid-cols-2 gap-4">
									<div className="flex flex-row items-center gap-4">
										{actionsValue?.complementaryActions?.types.map(
											(type: string, index: number) => (
												<div
													key={`comp-${type}`}
													className="flex flex-row items-center gap-1"
												>
													{index > 0 && <div className="">et </div>}
													<Checkbox
														id={`action-type-${type}`}
														label={getActionTypeLabel(type)}
														checked={true}
														disabled={isForm}
														className="border-gray-300 disabled:bg-gray-100"
													/>
												</div>
											),
										)}
									</div>
									{actionsValue?.complementaryActions?.buildingType && (
										<Checkbox
											id="building-type"
											label={getBuildingTypeLabel(
												actionsValue.complementaryActions.buildingType,
											)}
											checked={true}
											disabled={isForm}
											className="border-gray-300 disabled:bg-gray-100"
										/>
									)}
								</div>
							</div>
						)}

					{actionsValue?.buildingType === "APPARTMENT" && appartmentDetails && (
						<div className="flex flex-row items-center gap-4">
							<div className="text-gray-700 w-1/4">situé à l'étage</div>
							<div className="grid grid-cols-2 gap-4">
								{appartmentDetails.fromFloor && (
									<Checkbox
										id="from-floor"
										label={`${appartmentDetails.fromFloor}`}
										checked={true}
										disabled={isForm}
										className="border-gray-300 disabled:bg-gray-100"
									/>
								)}
								{appartmentDetails.toFloor && (
									<Checkbox
										id="to-floor"
										label={`sur ${appartmentDetails.toFloor}`}
										checked={true}
										disabled={isForm}
										className="border-gray-300 disabled:bg-gray-100"
									/>
								)}
							</div>
						</div>
					)}

					{currentBuildingDetails && (
						<div className="flex flex-row items-center gap-4">
							<div className="text-gray-700 w-1/4">Comprenant</div>
							<div className="grid grid-cols-2 gap-4">
								{"buildingsCount" in currentBuildingDetails &&
									currentBuildingDetails.buildingsCount && (
										<Checkbox
											id="buildings-count"
											label={formatBuildingLabel(
												Number(currentBuildingDetails.buildingsCount),
											)}
											checked={true}
											disabled={isForm}
											className="border-gray-300 disabled:bg-gray-100"
										/>
									)}
								{"floorsCount" in currentBuildingDetails &&
									currentBuildingDetails.floorsCount && (
										<Checkbox
											id="floors-count"
											label={`${currentBuildingDetails.floorsCount} étages (R+)`}
											checked={true}
											disabled={isForm}
											className="border-gray-300 disabled:bg-gray-100"
										/>
									)}
								{"basementsCount" in currentBuildingDetails &&
									currentBuildingDetails.basementsCount && (
										<Checkbox
											id="basements-count"
											label={`${currentBuildingDetails.basementsCount} sous-sol${Number(currentBuildingDetails.basementsCount) > 1 ? "s" : ""} (R-)`}
											checked={true}
											disabled={isForm}
											className="border-gray-300 disabled:bg-gray-100"
										/>
									)}
								{"maxFloors" in currentBuildingDetails &&
									currentBuildingDetails.maxFloors && (
										<Checkbox
											id="max-floors"
											label={`${currentBuildingDetails.maxFloors} étage(s) maximum`}
											checked={true}
											disabled={isForm}
											className="border-gray-300 disabled:bg-gray-100"
										/>
									)}
								{"maxBasements" in currentBuildingDetails &&
									currentBuildingDetails.maxBasements && (
										<Checkbox
											id="max-basements"
											label={`${currentBuildingDetails.maxBasements} sous-sol(s) maximum`}
											checked={true}
											disabled={isForm}
											className="border-gray-300 disabled:bg-gray-100"
										/>
									)}
							</div>
						</div>
					)}

					{currentBuildingDetails?.surface && (
						<div className="flex flex-row items-center gap-4">
							<div className="text-gray-700 w-1/4">
								La surface totale du bâtiment est de
							</div>
							<Checkbox
								id="surface-total"
								label={formatSurfaceLabel(
									Number(currentBuildingDetails.surface),
								)}
								checked={true}
								disabled={isForm}
								className="border-gray-300 disabled:bg-gray-100"
							/>
						</div>
					)}

					{currentBuildingDetails &&
						"habitationsCount" in currentBuildingDetails &&
						currentBuildingDetails.habitationsCount && (
							<div className="flex flex-row items-center gap-4">
								<div className="text-gray-700 w-1/4">L'opération concerne</div>
								<Checkbox
									id="habitations"
									label={formatDwellingLabel(
										Number(currentBuildingDetails.habitationsCount),
									)}
									checked={true}
									disabled={isForm}
									className="border-gray-300 disabled:bg-gray-100"
								/>
							</div>
						)}

					{actionsValue?.buildingType === "MIXT_BUILDING" &&
						mixtBuildingDetails &&
						(mixtBuildingDetails.habitationsSize ||
							mixtBuildingDetails.officesSize ||
							mixtBuildingDetails.commercialBuildingsSize) && (
							<div className="flex flex-row items-center gap-4">
								<div className="text-gray-700 w-1/4">et se décompose en</div>
								<div className="grid grid-cols-2 gap-4">
									{mixtBuildingDetails.habitationsSize && (
										<Checkbox
											id="habitations-size"
											label={formatSurfaceLabel(
												Number(mixtBuildingDetails.habitationsSize),
												"habitations",
											)}
											checked={true}
											disabled={isForm}
											className="border-gray-300 disabled:bg-gray-100"
										/>
									)}
									{mixtBuildingDetails.officesSize && (
										<Checkbox
											id="offices-size"
											label={formatSurfaceLabel(
												Number(mixtBuildingDetails.officesSize),
												"offices",
											)}
											checked={true}
											disabled={isForm}
											className="border-gray-300 disabled:bg-gray-100"
										/>
									)}
									{mixtBuildingDetails.commercialBuildingsSize && (
										<Checkbox
											id="commercial-size"
											label={formatSurfaceLabel(
												Number(mixtBuildingDetails.commercialBuildingsSize),
												"commercial",
											)}
											checked={true}
											disabled={isForm}
											className="border-gray-300 disabled:bg-gray-100"
										/>
									)}
								</div>
							</div>
						)}

					{actionsValue?.interventionOnLoadBearingStructure && (
						<div className="flex flex-row items-center gap-4">
							<div className="text-gray-700 w-1/4">Intervention spéciale</div>
							<Checkbox
								id="intervention-load-bearing"
								label={
									RENOVATION_OR_EXTENSION_ACTION_TYPES.interventionOnLoadBearingStructure
								}
								checked={true}
								disabled={isForm}
								className="border-gray-300 disabled:bg-gray-100"
							/>
						</div>
					)}

					{actionsValue?.underpinningExistingFoundations && (
						<div className="flex flex-row items-center gap-4">
							<div className="text-gray-700 w-1/4">Fondations</div>
							<Checkbox
								id="underpinning-foundations"
								label={
									RENOVATION_OR_EXTENSION_ACTION_TYPES.underpinningExistingFoundations
								}
								checked={true}
								disabled={isForm}
								className="border-gray-300 disabled:bg-gray-100"
							/>
						</div>
					)}
				</div>
			)}
			{showConstructionCost && (
				<div>
					<Input
						disabled
						label={(() => {
							const taxeModeLabel = taxMode === "TTC" ? "TTC" : "HT";
							const label = isReception
								? "Coût de construction"
								: "Coût de construction prévisionnel";
							return `${label} ${taxeModeLabel}`;
						})()}
						value={
							isReception
								? finalTotalAmount
								: formatCurrencyPrecise(constructionCost)
						}
					/>
				</div>
			)}
		</Card>
	);
};
